import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, apiClient } from '../baseQuery';
import { adminBase } from './adminBase';

export interface RatingSummary {
	average: number | null;
	count: number;
	/** 1–5 star histogram; index 0 = 1 star. */
	distribution: number[];
}

export interface SkillScopeRow {
	id: string;
	name: string;
	/** Class name for students, school name for classes. */
	context?: string;
	studentsScored: number;
	overall: number | null;
	skills: Record<string, number | null>;
	rating: RatingSummary;
	textFeedbackCount: number;
	/** Student rows only, newest first. */
	textFeedback?: { question: string; answer: string; at: string }[];
	/** Class rows only: the owning school. */
	schoolId?: string;
}

export interface MonthlyRating extends RatingSummary {
	/** 'YYYY-MM' */
	month: string;
}

export interface SkillRatingFilters {
	schoolId?: string;
	classId?: string;
	studentId?: string;
}

export interface SkillRatingAnalytics {
	skills: { key: string; name: string }[];
	levels: { value: number; label: string }[];
	maxLevel: number;
	platform: {
		studentsScored: number;
		overall: number | null;
		radar: { key: string; name: string; level: number | null; sampleCount: number }[];
		rating: RatingSummary;
	};
	schools: SkillScopeRow[];
	classes: SkillScopeRow[];
	students: SkillScopeRow[];
	/** Ratings per month for the current filter, newest first. */
	monthly: MonthlyRating[];
}

export type TrackerStatus = 'not_started' | 'in_progress' | 'paused' | 'completed';
export type TrackerStatusCounts = Record<TrackerStatus, number>;

export interface CurriculumTrackerFilters {
	schoolId?: string;
	classId?: string;
	trainerId?: string;
	curriculumId?: string;
	status?: TrackerStatus;
}

export interface TrackerAttentionItem {
	kind:
		| 'stalled'
		| 'late_start'
		| 'missed_periods'
		| 'students_behind'
		| 'paused'
		| 'no_curriculum';
	severity: number;
	reason: string;
	schoolId: string;
	schoolName: string;
	classId: string;
	className: string;
	curriculumId: string | null;
	curriculumTitle: string | null;
}

export interface TrackerPair {
	curriculumId: string;
	curriculumTitle: string;
	gradeLevel: string;
	status: TrackerStatus;
	totalSessions: number;
	completedSessions: number;
	progressPct: number;
	nextSessionTitle: string | null;
	periodsTaught: number;
	lastTaughtAt: string | null;
	daysSinceTaught: number | null;
	studentsBehind: number;
}

export interface TrackerSchoolRow {
	schoolId: string;
	schoolName: string;
	classes: number;
	assignments: number;
	statusCounts: TrackerStatusCounts;
	avgProgressPct: number | null;
	studentsBehind: number;
	attention: number;
}

export interface TrackerClassRow {
	classId: string;
	className: string;
	schoolId: string;
	schoolName: string;
	trainers: { trainerId: string; name: string }[];
	students: number;
	missedLast30: number;
	curricula: TrackerPair[];
}

export interface TrackerStudentRow {
	studentId: string;
	fullName: string;
	rollNumber: string;
	alforaaId: string;
	lastActive: string | null;
	avgQuizScore: number | null;
	totalGap: number;
	curricula: {
		curriculumId: string;
		curriculumTitle: string;
		completedSessions: number;
		totalSessions: number;
		progressPct: number;
		gap: number;
	}[];
}

export interface CurriculumTrackerData {
	summary: {
		statusCounts: TrackerStatusCounts;
		assignments: number;
		classes: number;
		classesWithoutCurriculum: number;
		avgProgressPct: number | null;
		studentsBehind: number;
	};
	attention: TrackerAttentionItem[];
	schools: TrackerSchoolRow[];
	classes: TrackerClassRow[];
	students: TrackerStudentRow[];
	options: {
		trainers: { trainerId: string; name: string }[];
		curricula: { curriculumId: string; title: string }[];
	};
}

export type DemoRequestStatus = 'new' | 'in_progress' | 'handled' | 'spam';

export interface DemoRequest {
	id: string;
	name: string;
	school: string;
	role: string;
	city: string;
	phone: string;
	email: string;
	message: string;
	source: string;
	status: DemoRequestStatus;
	handledAt: string | null;
	createdAt: string;
	updatedAt: string;
	notes: { text: string; byName: string; at: string }[];
	history: { from: DemoRequestStatus; to: DemoRequestStatus; byName: string; at: string }[];
}

export interface DemoRequestList {
	requests: DemoRequest[];
	total: number;
	page: number;
	limit: number;
	counts: Record<DemoRequestStatus | 'all', number>;
}

export const masterAdminApi = createApi({
	reducerPath: 'masterAdminApi',
	baseQuery: axiosBaseQuery,
	tagTypes: [
		'CatalogItems',
		'CatalogMappings',
		'AdminDashboard',
		'Schools',
		'SchoolDetail',
		'SchoolTrainers',
		'SchoolClasses',
		'SchoolClassStudents',
		'Users',
		'Curricula',
		'CurriculumDetail',
		'AuditLogs',
		'PlatformAnalytics',
		'FeatureFlags',
		'DemoRequests',
	],
	endpoints: (builder) => ({
		// --- Kit & machine catalog (PRD §3c) ---
		listCatalogItems: builder.query<any, { kind?: string } | void>({
			query: (params) => ({ url: `${adminBase()}/catalog/items`, params: params || undefined }),
			providesTags: ['CatalogItems'],
		}),
		createCatalogItem: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: `${adminBase()}/catalog/items`, method: 'POST', data: body }),
			invalidatesTags: ['CatalogItems'],
		}),
		updateCatalogItem: builder.mutation<any, { id: string } & Record<string, unknown>>({
			query: ({ id, ...body }) => ({ url: `${adminBase()}/catalog/items/${id}`, method: 'PATCH', data: body }),
			invalidatesTags: ['CatalogItems'],
		}),
		deactivateCatalogItem: builder.mutation<any, string>({
			query: (id) => ({ url: `${adminBase()}/catalog/items/${id}`, method: 'DELETE' }),
			invalidatesTags: ['CatalogItems'],
		}),
		listCatalogMappings: builder.query<any, void>({
			query: () => ({ url: `${adminBase()}/catalog/mappings` }),
			providesTags: ['CatalogMappings'],
		}),
		setCatalogMapping: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: `${adminBase()}/catalog/mappings`, method: 'PUT', data: body }),
			invalidatesTags: ['CatalogMappings'],
		}),
		bulkAssignCatalog: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: `${adminBase()}/catalog/mappings/bulk`, method: 'POST', data: body }),
			invalidatesTags: ['CatalogMappings'],
		}),
		cloneCatalogMapping: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: `${adminBase()}/catalog/mappings/clone`, method: 'POST', data: body }),
			invalidatesTags: ['CatalogMappings'],
		}),
		getKitDistribution: builder.query<any, Record<string, string> | void>({
			query: (params) => ({ url: `${adminBase()}/catalog/distribution`, params: params || undefined }),
		}),
		// ── Dashboard ─
		getAdminDashboard: builder.query<any, void>({
			query: () => ({ url: `${adminBase()}/dashboard` }),
			providesTags: ['AdminDashboard'],
		}),

		// ── Schools ─
		getSchools: builder.query<any, Record<string, any> | void>({
			query: (params) => ({
				url: `${adminBase()}/schools`,
				params: params || undefined,
			}),
			providesTags: ['Schools'],
		}),
		getSchoolOptions: builder.query<
			{ id: string; name: string; city: string }[],
			void
		>({
			query: () => ({ url: `${adminBase()}/schools/options` }),
			providesTags: ['Schools'],
		}),
		getSchool: builder.query<any, string>({
			query: (id) => ({ url: `${adminBase()}/schools/${id}` }),
			providesTags: (_result, _err, id) => [{ type: 'SchoolDetail', id }],
		}),
		searchSchoolEntities: builder.query<any, string>({
			query: (q) => ({
				url: `${adminBase()}/schools/search-all`,
				params: { query: q },
			}),
		}),
		createSchool: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: `${adminBase()}/schools`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['Schools', 'AdminDashboard'],
		}),
		updateSchool: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `${adminBase()}/schools/${id}`,
				method: 'PUT',
				data,
			}),
			invalidatesTags: (_result, _err, { id }) => [
				{ type: 'SchoolDetail', id },
				'Schools',
			],
		}),
		updateSchoolStatus: builder.mutation<any, { id: string; status: string }>({
			query: ({ id, status }) => ({
				url: `${adminBase()}/schools/${id}/status`,
				method: 'PATCH',
				data: { status },
			}),
			invalidatesTags: (_result, _err, { id }) => [
				{ type: 'SchoolDetail', id },
				'Schools',
			],
		}),
		deactivateSchool: builder.mutation<any, string>({
			query: (id) => ({
				url: `${adminBase()}/schools/${id}/deactivate`,
				method: 'PATCH',
			}),
			invalidatesTags: (_result, _err, id) => [
				{ type: 'SchoolDetail', id },
				'Schools',
			],
		}),
		deleteSchool: builder.mutation<any, string>({
			query: (id) => ({ url: `${adminBase()}/schools/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Schools', 'AdminDashboard'],
		}),

		// ── School Entities (trainers/classes/students within a school) ─
		getAdminSchoolTrainers: builder.query<any, string>({
			query: (schoolId) => ({
				url: `${adminBase()}/schools/${schoolId}/trainers`,
			}),
			providesTags: (_result, _err, schoolId) => [
				{ type: 'SchoolTrainers', id: schoolId },
			],
		}),
		getAdminSchoolClasses: builder.query<any, string>({
			query: (schoolId) => ({
				url: `${adminBase()}/schools/${schoolId}/classes`,
			}),
			providesTags: (_result, _err, schoolId) => [
				{ type: 'SchoolClasses', id: schoolId },
			],
		}),
		getAdminClassStudents: builder.query<
			any,
			{ schoolId: string; classId: string }
		>({
			query: ({ schoolId, classId }) => ({
				url: `${adminBase()}/schools/${schoolId}/classes/${classId}/students`,
			}),
			providesTags: (_result, _err, { classId }) => [
				{ type: 'SchoolClassStudents', id: classId },
			],
		}),
		createAdminSchoolTrainer: builder.mutation<
			any,
			{ schoolId: string; data: Record<string, unknown> }
		>({
			query: ({ schoolId, data }) => ({
				url: `${adminBase()}/schools/${schoolId}/trainers`,
				method: 'POST',
				data,
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolTrainers', id: schoolId },
				{ type: 'SchoolDetail', id: schoolId },
			],
		}),
		deleteAdminSchoolTrainer: builder.mutation<
			any,
			{ schoolId: string; trainerId: string }
		>({
			query: ({ schoolId, trainerId }) => ({
				url: `${adminBase()}/schools/${schoolId}/trainers/${trainerId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolTrainers', id: schoolId },
				{ type: 'SchoolDetail', id: schoolId },
			],
		}),
		assignAdminSchoolClass: builder.mutation<
			any,
			{ schoolId: string; trainerId: string; classId: string }
		>({
			query: ({ schoolId, trainerId, classId }) => ({
				url: `${adminBase()}/schools/${schoolId}/trainers/${trainerId}/assign-class`,
				method: 'POST',
				data: { classId },
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolTrainers', id: schoolId },
				{ type: 'SchoolClasses', id: schoolId },
			],
		}),
		attachAdminSchoolTrainer: builder.mutation<
			any,
			{ schoolId: string; email: string }
		>({
			query: ({ schoolId, email }) => ({
				url: `${adminBase()}/schools/${schoolId}/trainers/attach`,
				method: 'POST',
				data: { email },
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolTrainers', id: schoolId },
				{ type: 'SchoolDetail', id: schoolId },
			],
		}),
		detachAdminSchoolTrainer: builder.mutation<
			any,
			{ schoolId: string; trainerId: string }
		>({
			query: ({ schoolId, trainerId }) => ({
				url: `${adminBase()}/schools/${schoolId}/trainers/${trainerId}/detach`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolTrainers', id: schoolId },
				{ type: 'SchoolDetail', id: schoolId },
			],
		}),
		createAdminSchoolClass: builder.mutation<
			any,
			{ schoolId: string; data: Record<string, unknown> }
		>({
			query: ({ schoolId, data }) => ({
				url: `${adminBase()}/schools/${schoolId}/classes`,
				method: 'POST',
				data,
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolClasses', id: schoolId },
				{ type: 'SchoolDetail', id: schoolId },
			],
		}),
		updateAdminSchoolClass: builder.mutation<
			any,
			{ schoolId: string; classId: string; data: Record<string, unknown> }
		>({
			query: ({ schoolId, classId, data }) => ({
				url: `${adminBase()}/schools/${schoolId}/classes/${classId}`,
				method: 'PUT',
				data,
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolClasses', id: schoolId },
			],
		}),
		deleteAdminSchoolClass: builder.mutation<
			any,
			{ schoolId: string; classId: string }
		>({
			query: ({ schoolId, classId }) => ({
				url: `${adminBase()}/schools/${schoolId}/classes/${classId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolClasses', id: schoolId },
				{ type: 'SchoolDetail', id: schoolId },
			],
		}),
		reassignClassTrainer: builder.mutation<
			any,
			{ schoolId: string; classId: string; trainerId: string }
		>({
			query: ({ schoolId, classId, trainerId }) => ({
				url: `${adminBase()}/schools/${schoolId}/classes/${classId}/trainer`,
				method: 'PATCH',
				data: { trainerId },
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolClasses', id: schoolId },
				{ type: 'SchoolTrainers', id: schoolId },
			],
		}),
		createAdminStudent: builder.mutation<
			any,
			{ schoolId: string; data: Record<string, unknown> }
		>({
			query: ({ schoolId, data }) => ({
				url: `${adminBase()}/schools/${schoolId}/students`,
				method: 'POST',
				data,
			}),
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolDetail', id: schoolId },
				{ type: 'SchoolClasses', id: schoolId },
			],
		}),
		updateAdminStudent: builder.mutation<
			any,
			{ studentId: string; data: Record<string, unknown> }
		>({
			query: ({ studentId, data }) => ({
				url: `${adminBase()}/students/${studentId}`,
				method: 'PUT',
				data,
			}),
		}),
		transferAdminStudentSection: builder.mutation<
			any,
			{ studentId: string; newClassId: string }
		>({
			query: ({ studentId, newClassId }) => ({
				url: `${adminBase()}/students/${studentId}/transfer-section`,
				method: 'PATCH',
				data: { newClassId },
			}),
			invalidatesTags: ['SchoolClassStudents'],
		}),
		getAdminParentCredentials: builder.query<
			{
				parentId: string;
				fullName: string;
				alforaaId: string;
				email: string;
				mustChangePassword: boolean;
			},
			string
		>({
			query: (studentId) => ({
				url: `${adminBase()}/students/${studentId}/parent-credentials`,
				method: 'GET',
			}),
		}),
		resetAdminParentPassword: builder.mutation<any, string>({
			query: (studentId) => ({
				url: `${adminBase()}/students/${studentId}/reset-parent-password`,
				method: 'POST',
			}),
		}),
		uploadAdminStudents: builder.mutation<
			any,
			{ schoolId: string; file: File }
		>({
			queryFn: async ({ schoolId, file }) => {
				try {
					const formData = new FormData();
					formData.append('file', file);
					const result = await apiClient.post(
						`${adminBase()}/schools/${schoolId}/students/upload`,
						formData,
						{
							// Don't set Content-Type header - let axios handle it for FormData
							// This ensures the boundary parameter is included correctly
							headers: { 'Content-Type': undefined },
						},
					);
					return { data: result.data };
				} catch (err: any) {
					return {
						error: {
							status: err.response?.status,
							data: err.response?.data,
							message: err.message,
						},
					};
				}
			},
			invalidatesTags: (_result, _err, { schoolId }) => [
				{ type: 'SchoolDetail', id: schoolId },
				{ type: 'SchoolClasses', id: schoolId },
				'SchoolClassStudents',
			],
		}),

		// ── Users ─
		listUsers: builder.query<any, Record<string, any> | void>({
			query: (params) => ({
				url: `${adminBase()}/users`,
				params: params || undefined,
			}),
			providesTags: ['Users'],
		}),
		searchUsers: builder.query<any, Record<string, any>>({
			query: (params) => ({ url: `${adminBase()}/users/search`, params }),
		}),
		getUser: builder.query<any, { role: string; id: string }>({
			query: ({ role, id }) => ({ url: `${adminBase()}/users/${role}/${id}` }),
		}),
		createUser: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: `${adminBase()}/users`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['Users', 'AdminDashboard'],
		}),
		resetCredentials: builder.mutation<
			any,
			{ role: string; id: string; data: Record<string, unknown> }
		>({
			query: ({ role, id, data }) => ({
				url: `${adminBase()}/users/${role}/${id}/reset-credentials`,
				method: 'POST',
				data,
			}),
		}),
		updateUserStatus: builder.mutation<
			any,
			{ role: string; id: string; isActive: boolean }
		>({
			query: ({ role, id, isActive }) => ({
				url: `${adminBase()}/users/${role}/${id}/status`,
				method: 'PATCH',
				data: { isActive },
			}),
			invalidatesTags: ['Users'],
		}),
		deactivateUser: builder.mutation<any, { role: string; id: string }>({
			query: ({ role, id }) => ({
				url: `${adminBase()}/users/${role}/${id}/deactivate`,
				method: 'PATCH',
			}),
			invalidatesTags: ['Users'],
		}),
		updateZonalManagerSchools: builder.mutation<
			any,
			{ id: string; schoolIds: string[] }
		>({
			query: ({ id, schoolIds }) => ({
				url: `${adminBase()}/zonal-managers/${id}/schools`,
				method: 'PATCH',
				data: { schoolIds },
			}),
			invalidatesTags: ['Users'],
		}),
		deleteUser: builder.mutation<any, { role: string; id: string }>({
			query: ({ role, id }) => ({
				url: `${adminBase()}/users/${role}/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Users', 'AdminDashboard'],
		}),

		// ── Curriculum ─
		getCurricula: builder.query<any, void>({
			query: () => ({ url: `${adminBase()}/curriculum` }),
			providesTags: ['Curricula'],
		}),
		getCurriculumDetail: builder.query<any, string>({
			query: (id) => ({ url: `${adminBase()}/curriculum/${id}` }),
			providesTags: (_result, _err, id) => [{ type: 'CurriculumDetail', id }],
		}),
		createCurriculum: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: `${adminBase()}/curriculum`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['Curricula'],
		}),
		updateCurriculum: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `${adminBase()}/curriculum/${id}`,
				method: 'PUT',
				data,
			}),
			invalidatesTags: (_result, _err, { id }) => [
				'Curricula',
				{ type: 'CurriculumDetail', id },
			],
		}),
		deleteCurriculum: builder.mutation<any, string>({
			query: (id) => ({
				url: `${adminBase()}/curriculum/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Curricula'],
		}),
		duplicateCurriculum: builder.mutation<any, string>({
			query: (id) => ({
				url: `${adminBase()}/curriculum/${id}/duplicate`,
				method: 'POST',
			}),
			invalidatesTags: ['Curricula'],
		}),
		assignCurriculum: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `${adminBase()}/curriculum/${id}/assign`,
				method: 'POST',
				data,
			}),
		}),
		assignCurriculumToClasses: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `${adminBase()}/curriculum/${id}/assign-classes`,
				method: 'POST',
				data,
			}),
			invalidatesTags: ['SchoolClasses'],
		}),

		// ── Analytics & Audit ─
		getPlatformAnalytics: builder.query<any, void>({
			query: () => ({ url: `${adminBase()}/analytics/platform` }),
			providesTags: ['PlatformAnalytics'],
		}),
		getSkillRatingAnalytics: builder.query<
			SkillRatingAnalytics,
			SkillRatingFilters | void
		>({
			query: (args) => ({
				url: `${adminBase()}/analytics/skills`,
				params: {
					...(args?.schoolId ? { school_id: args.schoolId } : {}),
					...(args?.classId ? { class_id: args.classId } : {}),
					...(args?.studentId ? { student_id: args.studentId } : {}),
				},
			}),
			providesTags: ['PlatformAnalytics'],
		}),
		// ── Website demo requests (master admin only — never the zonal prefix) ─
		listDemoRequests: builder.query<
			DemoRequestList,
			{ status?: DemoRequestStatus; q?: string; from?: string; to?: string; page?: number; limit?: number }
		>({
			query: (params) => ({ url: '/master-admin/demo-requests', params }),
			providesTags: ['DemoRequests'],
		}),
		updateDemoRequestStatus: builder.mutation<
			DemoRequest,
			{ id: string; status: DemoRequestStatus; note?: string }
		>({
			query: ({ id, ...data }) => ({
				url: `/master-admin/demo-requests/${id}/status`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['DemoRequests'],
		}),
		addDemoRequestNote: builder.mutation<DemoRequest, { id: string; text: string }>({
			query: ({ id, text }) => ({
				url: `/master-admin/demo-requests/${id}/notes`,
				method: 'POST',
				data: { text },
			}),
			invalidatesTags: ['DemoRequests'],
		}),
		getCurriculumTracker: builder.query<
			CurriculumTrackerData,
			CurriculumTrackerFilters | void
		>({
			query: (args) => ({
				url: `${adminBase()}/analytics/curriculum-tracker`,
				params: {
					...(args?.schoolId ? { school_id: args.schoolId } : {}),
					...(args?.classId ? { class_id: args.classId } : {}),
					...(args?.trainerId ? { trainer_id: args.trainerId } : {}),
					...(args?.curriculumId ? { curriculum_id: args.curriculumId } : {}),
					...(args?.status ? { status: args.status } : {}),
				},
			}),
			providesTags: ['PlatformAnalytics'],
		}),
		getClassCurriculumProgress: builder.query<
			any,
			{ classId: string; curriculumId: string }
		>({
			query: ({ classId, curriculumId }) => ({
				url: `${adminBase()}/analytics/class/${classId}/curriculum/${curriculumId}`,
			}),
		}),
		getIntegrationStatus: builder.query<any, void>({
			query: () => ({ url: '/integrations/status' }),
		}),
		getGallery: builder.query<
			{
				photos: {
					url: string;
					source: 'student' | 'trainer';
					takenAt: string;
					studentName: string;
					className: string;
					classId: string | null;
				}[];
				nextCursor: string | null;
			},
			Record<string, any>
		>({
			query: (params) => ({ url: `${adminBase()}/gallery`, params }),
			// Each cursor page is appended to the previous ones, so pages of the
			// same feed must share a cache entry. The cursor is excluded from the
			// key; changing a filter starts a new feed.
			serializeQueryArgs: ({ queryArgs, endpointName }) => {
				const { cursor: _cursor, ...filters } = queryArgs;
				return `${endpointName}(${JSON.stringify(filters)})`;
			},
			merge: (current, incoming, { arg }) => {
				// No cursor means this is a fresh feed — replace rather than append.
				if (!arg.cursor) return incoming;
				current.photos.push(...incoming.photos);
				current.nextCursor = incoming.nextCursor;
			},
			forceRefetch: ({ currentArg, previousArg }) =>
				currentArg?.cursor !== previousArg?.cursor,
		}),
		getAuditLogs: builder.query<any, Record<string, any> | void>({
			query: (params) => ({
				url: `${adminBase()}/audit-logs`,
				params: params || undefined,
			}),
			providesTags: ['AuditLogs'],
		}),
		getIpGeo: builder.query<
			{
				ip: string;
				geo: {
					city: string | null;
					region: string | null;
					country: string | null;
					timezone: string | null;
					ll: [number, number] | null;
					label: string;
				} | null;
			},
			string
		>({
			query: (ip) => ({
				url: `${adminBase()}/audit-logs/ip-geo`,
				params: { ip },
			}),
		}),

		// ── Config ─
		getFeatureFlags: builder.query<any, void>({
			query: () => ({ url: `${adminBase()}/config/feature-flags` }),
			providesTags: ['FeatureFlags'],
		}),
		updateFeatureFlags: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: `${adminBase()}/config/feature-flags`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['FeatureFlags'],
		}),

		// ── Master Admin Management ─
		createMasterAdmin: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: `${adminBase()}/master-admins`,
				method: 'POST',
				data: body,
			}),
		}),

		// ── Resend School Admin Credentials ─
		resendSchoolAdminCredentials: builder.mutation<
			any,
			{ schoolId: string; adminIds: string[] }
		>({
			query: ({ schoolId, adminIds }) => ({
				url: `${adminBase()}/schools/${schoolId}/resend-admin-credentials`,
				method: 'POST',
				data: { adminIds },
			}),
		}),

		// ── School Admin Permissions ─
		updateSchoolAdminPermissions: builder.mutation<
			any,
			{
				schoolId: string;
				adminId: string;
				permissions: {
					timetableView?: boolean;
					timetableEdit?: boolean;
					monitoringView?: boolean;
					missedReasonReview?: boolean;
				};
			}
		>({
			query: ({ adminId, permissions }) => ({
				url: `${adminBase()}/school-admins/${adminId}/permissions`,
				method: 'PATCH',
				data: { permissions },
			}),
			invalidatesTags: (_r, _e, { schoolId }) => [
				{ type: 'SchoolDetail', id: schoolId },
			],
		}),

		// ── Student Session Review & Curriculum Progress ─
		getAdminStudentSessionReview: builder.query<
			any,
			{ studentId: string; sessionId: string }
		>({
			query: ({ studentId, sessionId }) => ({
				url: `${adminBase()}/students/${studentId}/sessions/${sessionId}/review`,
			}),
		}),
		getAdminStudentCurriculumProgress: builder.query<any, string>({
			query: (studentId) => ({
				url: `${adminBase()}/students/${studentId}/curriculum-progress`,
			}),
		}),
	}),
});

export const {
	// Queries
	useGetAdminDashboardQuery,
	useGetSchoolsQuery,
	useGetSchoolOptionsQuery,
	useGetSchoolQuery,
	useLazySearchSchoolEntitiesQuery,
	useCreateSchoolMutation,
	useUpdateSchoolMutation,
	useUpdateSchoolStatusMutation,
	useDeactivateSchoolMutation,
	useDeleteSchoolMutation,
	useGetAdminSchoolTrainersQuery,
	useGetAdminSchoolClassesQuery,
	useGetAdminClassStudentsQuery,
	useLazyGetAdminClassStudentsQuery,
	useCreateAdminSchoolTrainerMutation,
	useDeleteAdminSchoolTrainerMutation,
	useAssignAdminSchoolClassMutation,
	useAttachAdminSchoolTrainerMutation,
	useDetachAdminSchoolTrainerMutation,
	useCreateAdminSchoolClassMutation,
	useUpdateAdminSchoolClassMutation,
	useDeleteAdminSchoolClassMutation,
	useReassignClassTrainerMutation,
	useCreateAdminStudentMutation,
	useUpdateAdminStudentMutation,
	useTransferAdminStudentSectionMutation,
	useGetAdminParentCredentialsQuery,
	useLazyGetAdminParentCredentialsQuery,
	useResetAdminParentPasswordMutation,
	useUploadAdminStudentsMutation,
	useListUsersQuery,
	useCreateUserMutation,
	useResetCredentialsMutation,
	useUpdateUserStatusMutation,
	useDeactivateUserMutation,
	useUpdateZonalManagerSchoolsMutation,
	useDeleteUserMutation,
	useGetCurriculaQuery,
	useGetCurriculumDetailQuery,
	useCreateCurriculumMutation,
	useUpdateCurriculumMutation,
	useDeleteCurriculumMutation,
	useDuplicateCurriculumMutation,
	useAssignCurriculumMutation,
	useAssignCurriculumToClassesMutation,
	useGetPlatformAnalyticsQuery,
	useGetSkillRatingAnalyticsQuery,
	useGetCurriculumTrackerQuery,
	useListDemoRequestsQuery,
	useUpdateDemoRequestStatusMutation,
	useAddDemoRequestNoteMutation,
	useGetClassCurriculumProgressQuery,
	useLazyGetClassCurriculumProgressQuery,
	useGetIntegrationStatusQuery,
	useGetGalleryQuery,
	useGetAuditLogsQuery,
	useGetIpGeoQuery,
	useGetFeatureFlagsQuery,
	useUpdateFeatureFlagsMutation,
	useCreateMasterAdminMutation,
	useResendSchoolAdminCredentialsMutation,
	useUpdateSchoolAdminPermissionsMutation,
	useGetAdminStudentSessionReviewQuery,
	useGetAdminStudentCurriculumProgressQuery,
	useListCatalogItemsQuery,
	useCreateCatalogItemMutation,
	useUpdateCatalogItemMutation,
	useDeactivateCatalogItemMutation,
	useListCatalogMappingsQuery,
	useSetCatalogMappingMutation,
	useBulkAssignCatalogMutation,
	useCloneCatalogMappingMutation,
	useGetKitDistributionQuery,
} = masterAdminApi;
